/**
 * ARPlacement - Touch-based scene placement controls for 8th Wall AR.
 *
 * Provides:
 *   - Tap/double-tap to place or move the scene on the ground
 *   - Pinch to scale, two-finger twist to rotate, both about the placed point
 *   - Explore-style cursor pulse at the placement point
 *   - Short instructions while the floor is found and the scene is placed
 *
 * Ground detection follows 8th Wall's docs: with XrController `scale: 'absolute'` the camera's
 * height is its real height above the ground, so the ground is the plane y = 0 and a tap is a ray
 * cast onto it (the official "placeground" sample). Feature-point hitTest landed scenes on tables
 * and walls, and crashed the engine before tracking was established.
 * https://8thwall.org/docs/api/engine/xrcontroller/configure
 *
 * The scene's own floor (the bottom of its visible bounds, ignoring stray splats) is what lands on
 * the tapped point, so a room scan stands on the real floor rather than hanging from its origin.
 */

import * as pc from 'playcanvas';
import { arInfo, arWarn } from './ARLogger';
import { computeRenderBounds } from '../../utils/renderBounds';

export type ARPlacementTapMode = 'single' | 'double' | 'none';

type GroundHit = { position: pc.Vec3 };

/** Taps further away than this (metres along the ray) are treated as missing the floor. */
const MAX_PLACE_DISTANCE = 25;

export interface ARPlacementConfig {
    /** PlayCanvas application */
    app: pc.Application;
    /** The AR camera (posed by 8th Wall) the tap ray is cast from */
    camera: pc.CameraComponent;
    /** Scene root entity (content to move/scale/rotate) */
    sceneRoot: pc.Entity;
    /** Canvas element for touch events */
    canvas: HTMLCanvasElement;
    /** Min scale factor (default: 0.01, so a building fits on a table) */
    minScale?: number;
    /** Max scale factor (default: 5.0) */
    maxScale?: number;
    /** Whether AR placement follows single-tap, double-tap, or disabled refocus behavior */
    tapMode?: ARPlacementTapMode;
    /** Return true when another AR interaction, such as a hotspot, should own this tap */
    shouldIgnoreTap?: (clientX: number, clientY: number) => boolean;
    /** Callback when scene is placed */
    onPlaced?: (position: pc.Vec3) => void;
    /** Show a short instruction over the AR view; null hides it */
    onHint?: (text: string | null) => void;
}

export class ARPlacement {
    private app: pc.Application;
    private camera: pc.CameraComponent;
    private sceneRoot: pc.Entity;
    private canvas: HTMLCanvasElement;
    private minScale: number;
    private maxScale: number;
    private tapMode: ARPlacementTapMode;
    private shouldIgnoreTap?: (clientX: number, clientY: number) => boolean;
    private onPlaced?: (position: pc.Vec3) => void;
    private onHint?: (text: string | null) => void;

    private isPlaced = false;
    /** Where the scene's floor sits, in sceneRoot space; lands on the tapped point. */
    private anchorLocal = new pc.Vec3();
    /** The tapped ground point the anchor is held on while scaling and rotating. */
    private placedAt = new pc.Vec3();
    private lastHint: string | null = null;
    private hintTimer: ReturnType<typeof setTimeout> | null = null;
    private reticle: pc.Entity | null = null;
    private reticleMat: pc.StandardMaterial | null = null;
    private reticleMesh: pc.Mesh | null = null;
    private reticleOpacity = 0;
    private reticleVisibleUntil = 0;
    private currentScale = 1;
    private trackingStatus = '';
    private trackingGateWarned = false;

    // Touch state for gestures
    private lastPinchDistance = 0;
    private lastRotationAngle = 0;
    private isTwoFingerGesture = false;
    private wasMultiTouch = false;
    private touchStartX = 0;
    private touchStartY = 0;
    private touchMoved = false;
    private lastTapTime = 0;
    private lastTouchHandledAt = 0;
    private readonly doubleTapThresholdMs = 300;
    private readonly tapMoveThresholdPx = 12;
    private readonly reticleHoldMs = 420;

    // Bound handlers for cleanup
    private boundTouchStart: (e: TouchEvent) => void;
    private boundTouchMove: (e: TouchEvent) => void;
    private boundTouchEnd: (e: TouchEvent) => void;
    private boundClick: (e: MouseEvent) => void;
    private boundDblClick: (e: MouseEvent) => void;

    constructor(config: ARPlacementConfig) {
        this.app = config.app;
        this.camera = config.camera;
        this.sceneRoot = config.sceneRoot;
        this.canvas = config.canvas;
        this.minScale = config.minScale ?? 0.01;
        this.maxScale = config.maxScale ?? 5.0;
        this.tapMode = config.tapMode ?? 'single';
        this.shouldIgnoreTap = config.shouldIgnoreTap;
        this.onPlaced = config.onPlaced;
        this.onHint = config.onHint;

        this.boundTouchStart = this.handleTouchStart.bind(this);
        this.boundTouchMove = this.handleTouchMove.bind(this);
        this.boundTouchEnd = this.handleTouchEnd.bind(this);
        this.boundClick = this.handleClick.bind(this);
        this.boundDblClick = this.handleDblClick.bind(this);
    }

    /** Initialize placement mode — listen for tap/double-tap relocation */
    start(): void {
        this.isPlaced = false;
        this.currentScale = 1;
        this.trackingGateWarned = false;
        this.reticleOpacity = 0;
        this.reticleVisibleUntil = 0;
        this.computeAnchor();
        this.updateHint();
        arInfo('AR placement controls started', {
            sceneRootEnabled: this.sceneRoot.enabled,
            sceneRootPosition: this.sceneRoot.getPosition(),
            tapMode: this.tapMode,
        });

        // Keep scene content visible at its current transform so the splat
        // renders immediately. Tap repositions it onto a detected surface.
        // (Previously we hid it until first tap, which left the viewer blank
        // if hit-testing never returned results.)
        this.sceneRoot.enabled = true;

        // Create the same ring-style cursor used by explore mode. It appears
        // only after a successful placement and then fades away.
        this.createReticle();

        // Listen for touch/click events
        this.canvas.addEventListener('touchstart', this.boundTouchStart, { passive: false });
        this.canvas.addEventListener('touchmove', this.boundTouchMove, { passive: false });
        this.canvas.addEventListener('touchend', this.boundTouchEnd, { passive: false });
        this.canvas.addEventListener('click', this.boundClick);
        this.canvas.addEventListener('dblclick', this.boundDblClick);

        // Start fading the cursor pulse each frame.
        this.app.on('update', this.updateReticlePulse, this);
    }

    /**
     * Update tracking status from the pipeline. hitTest must not run until the
     * SLAM module has established a point cloud — calling hitTest in LIMITED
     * or UNKNOWN state crashes the WASM binary with "memory access out of
     * bounds" (consistently reproducible in the desktop 8th Wall emulator).
     */
    setTrackingStatus(status: string): void {
        this.trackingStatus = status;
        this.updateHint();
    }

    /** Keep AR tap behavior in sync with the viewer's refocus setting. */
    setTapMode(mode: ARPlacementTapMode): void {
        this.tapMode = mode;
        this.lastTapTime = 0;
    }

    /** Stop placement mode and clean up */
    stop(): void {
        arInfo('AR placement controls stopped', {
            wasPlaced: this.isPlaced,
            sceneRootPosition: this.sceneRoot.getPosition(),
            sceneRootScale: this.sceneRoot.getLocalScale(),
        });
        this.canvas.removeEventListener('touchstart', this.boundTouchStart);
        this.canvas.removeEventListener('touchmove', this.boundTouchMove);
        this.canvas.removeEventListener('touchend', this.boundTouchEnd);
        this.canvas.removeEventListener('click', this.boundClick);
        this.canvas.removeEventListener('dblclick', this.boundDblClick);

        this.app.off('update', this.updateReticlePulse, this);
        if (this.hintTimer) clearTimeout(this.hintTimer);
        this.hintTimer = null;
        this.setHint(null);

        if (this.reticle) {
            this.reticle.destroy();
            this.reticle = null;
        }
        this.reticleMat?.destroy();
        this.reticleMat = null;
        this.reticleMesh?.destroy();
        this.reticleMesh = null;
    }

    /** Create the same white torus cursor used by normal explore mode. */
    private createReticle(): void {
        this.reticle = new pc.Entity('ar-reticle');

        const material = new pc.StandardMaterial();
        material.emissive = new pc.Color(1, 1, 1);
        material.diffuse = new pc.Color(0, 0, 0);
        material.useLighting = false;
        material.opacity = 1;
        material.blendType = pc.BLEND_NORMAL;
        material.depthTest = false;
        material.depthWrite = false;
        material.cull = pc.CULLFACE_NONE;
        material.update();
        this.reticleMat = material;

        const geom = new pc.TorusGeometry({ tubeRadius: 0.048, ringRadius: 0.193, segments: 24, sides: 10 });
        this.reticleMesh = pc.Mesh.fromGeometry(this.app.graphicsDevice, geom);
        const meshInstance = new pc.MeshInstance(this.reticleMesh, material);
        meshInstance.drawOrder = 9999;
        this.reticle.addComponent('render', {
            meshInstances: [meshInstance],
            castShadows: false,
            receiveShadows: false,
        });
        this.reticle.setLocalScale(0.72, 0.32, 0.72);
        this.reticle.enabled = false;

        this.app.root.addChild(this.reticle);
    }

    /** Fade the cursor pulse after each successful placement. */
    private updateReticlePulse(dt: number): void {
        if (!this.reticle || !this.reticleMat) return;

        const targetOpacity = performance.now() < this.reticleVisibleUntil ? 1 : 0;
        const speed = targetOpacity > this.reticleOpacity ? 16 : 5;
        this.reticleOpacity += (targetOpacity - this.reticleOpacity) * Math.min(1, dt * speed);
        if (Math.abs(this.reticleOpacity - targetOpacity) < 0.01) {
            this.reticleOpacity = targetOpacity;
        }

        if (this.reticleOpacity > 0) {
            this.reticleMat.opacity = this.reticleOpacity * 0.85;
            this.reticleMat.update();
            this.reticle.enabled = true;
        } else {
            this.reticle.enabled = false;
        }
    }

    /**
     * The scene's floor in sceneRoot space: the bottom-centre of its visible bounds (splats from
     * their 2nd-98th percentile, so stray splats don't count). Falls back to the root's origin.
     */
    private computeAnchor(): void {
        const bounds = computeRenderBounds(this.sceneRoot);
        if (!bounds) {
            this.anchorLocal.set(0, 0, 0);
            return;
        }
        const floorWorld = new pc.Vec3(bounds.center[0], bounds.min[1], bounds.center[2]);
        const toLocal = this.sceneRoot.getWorldTransform().clone().invert();
        toLocal.transformPoint(floorWorld, this.anchorLocal);
    }

    private setHint(text: string | null): void {
        if (text === this.lastHint) return;
        this.lastHint = text;
        this.onHint?.(text);
    }

    private updateHint(): void {
        if (this.isPlaced) return;
        this.setHint(this.trackingStatus === 'NORMAL'
            ? 'Tap the floor to place the scene'
            : 'Move your phone slowly so AR can find the floor');
    }

    /**
     * The point on the ground (y = 0 with absolute scale) under a screen point, or null when the
     * floor isn't known yet or the ray misses it (pointing at the horizon or up).
     */
    private getGroundHitAtClientPoint(clientX: number, clientY: number): GroundHit | null {
        // Until tracking is NORMAL the camera's height above the floor isn't established.
        if (this.trackingStatus !== 'NORMAL') {
            if (!this.trackingGateWarned) {
                this.trackingGateWarned = true;
                arWarn('AR placement tap ignored until XR8 tracking is NORMAL', { status: this.trackingStatus });
            }
            this.updateHint();
            return null;
        }

        const rect = this.canvas.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) return null;
        const x = clientX - rect.left;
        const y = clientY - rect.top;
        const from = this.camera.screenToWorld(x, y, this.camera.nearClip);
        const to = this.camera.screenToWorld(x, y, this.camera.farClip);
        const dir = to.sub(from).normalize();
        if (from.y <= 0.01 || dir.y > -1e-3) return null;
        const t = -from.y / dir.y;
        if (!(t > 0) || t > MAX_PLACE_DISTANCE) return null;
        return { position: from.add(dir.mulScalar(t)) };
    }

    /** Put the scene's floor anchor on the placed point at the current scale and rotation. */
    private applyPlacement(): void {
        const s = this.currentScale;
        this.sceneRoot.setLocalScale(s, s, s);
        const offset = this.sceneRoot.getRotation().transformVector(
            new pc.Vec3(this.anchorLocal.x * s, this.anchorLocal.y * s, this.anchorLocal.z * s),
        );
        this.sceneRoot.setPosition(this.placedAt.x - offset.x, this.placedAt.y - offset.y, this.placedAt.z - offset.z);
    }

    private shouldSkipTap(clientX: number, clientY: number): boolean {
        return this.tapMode === 'none' || !!this.shouldIgnoreTap?.(clientX, clientY);
    }

    /** Handle tap to place scene */
    private handleClick(e: MouseEvent): void {
        if (this.tapMode !== 'single') return;
        if (Date.now() - this.lastTouchHandledAt < 500) return;
        this.placeAtClientPoint(e.clientX, e.clientY);
    }

    private handleDblClick(e: MouseEvent): void {
        if (this.tapMode !== 'double') return;
        if (Date.now() - this.lastTouchHandledAt < 500) return;
        this.placeAtClientPoint(e.clientX, e.clientY);
    }

    private handleTouchStart(e: TouchEvent): void {
        if (e.touches.length > 1) {
            // Start two-finger gesture (pinch/rotate)
            this.wasMultiTouch = true;
            if (e.touches.length === 2) {
                if (this.isPlaced) e.preventDefault();
                this.isTwoFingerGesture = true;
                this.lastPinchDistance = this.getTouchDistance(e.touches);
                this.lastRotationAngle = this.getTouchAngle(e.touches);
            }
        } else if (e.touches.length === 1) {
            this.touchStartX = e.touches[0].clientX;
            this.touchStartY = e.touches[0].clientY;
            this.touchMoved = false;
        }
    }

    private handleTouchMove(e: TouchEvent): void {
        if (e.touches.length === 1 && !this.isTwoFingerGesture) {
            const dx = e.touches[0].clientX - this.touchStartX;
            const dy = e.touches[0].clientY - this.touchStartY;
            if ((dx * dx + dy * dy) > (this.tapMoveThresholdPx * this.tapMoveThresholdPx)) {
                this.touchMoved = true;
            }
            return;
        }

        if (!this.isPlaced || e.touches.length !== 2 || !this.isTwoFingerGesture) return;
        e.preventDefault();

        // Pinch to scale
        const currentDistance = this.getTouchDistance(e.touches);
        if (this.lastPinchDistance > 0) {
            const scaleFactor = currentDistance / this.lastPinchDistance;
            this.currentScale = Math.max(this.minScale, Math.min(this.maxScale, this.currentScale * scaleFactor));
        }
        this.lastPinchDistance = currentDistance;

        // Two-finger rotate
        const currentAngle = this.getTouchAngle(e.touches);
        const angleDelta = currentAngle - this.lastRotationAngle;
        if (Math.abs(angleDelta) < Math.PI) { // Avoid 360 wrap-around jumps
            this.sceneRoot.rotateLocal(0, -angleDelta * (180 / Math.PI), 0);
        }
        this.lastRotationAngle = currentAngle;
        // Scale and turn about the placed point, so the scene stays where it was put.
        this.applyPlacement();
    }

    private handleTouchEnd(e: TouchEvent): void {
        if (e.touches.length < 2) {
            this.isTwoFingerGesture = false;
            this.lastPinchDistance = 0;
        }

        if (this.wasMultiTouch) {
            if (e.touches.length === 0) {
                this.wasMultiTouch = false;
            }
            this.lastTapTime = 0;
            return;
        }

        if (e.changedTouches.length !== 1 || this.touchMoved) return;

        const touch = e.changedTouches[0];
        if (this.shouldSkipTap(touch.clientX, touch.clientY)) return;

        if (this.tapMode === 'single') {
            e.preventDefault();
            this.lastTouchHandledAt = Date.now();
            this.placeAtClientPoint(touch.clientX, touch.clientY);
            return;
        }

        if (this.tapMode === 'double') {
            const now = Date.now();
            if (now - this.lastTapTime < this.doubleTapThresholdMs) {
                e.preventDefault();
                this.lastTouchHandledAt = now;
                this.lastTapTime = 0;
                this.placeAtClientPoint(touch.clientX, touch.clientY);
            } else {
                this.lastTapTime = now;
            }
        }
    }

    /** Place or move the scene onto the ground under the tap. */
    private placeAtClientPoint(clientX: number, clientY: number): void {
        if (this.shouldSkipTap(clientX, clientY)) return;
        const hit = this.getGroundHitAtClientPoint(clientX, clientY);
        if (!hit) return;

        const firstPlacement = !this.isPlaced;
        this.isPlaced = true;
        this.placedAt.copy(hit.position);
        this.applyPlacement();
        this.sceneRoot.enabled = true;
        const pos = hit.position.clone();

        this.showReticleAtHit(hit);
        if (firstPlacement) {
            this.setHint('Pinch to resize \u00b7 twist with two fingers to turn \u00b7 tap to move');
            if (this.hintTimer) clearTimeout(this.hintTimer);
            this.hintTimer = setTimeout(() => { this.hintTimer = null; this.setHint(null); }, 4000);
        }

        this.onPlaced?.(pos.clone());
        arInfo('AR scene placed/moved', {
            position: { x: pos.x, y: pos.y, z: pos.z },
            scale: this.currentScale,
            tapMode: this.tapMode,
        });
    }

    private showReticleAtHit(hit: GroundHit): void {
        if (!this.reticle || !this.reticleMat) return;

        // Flat on the ground plane.
        this.reticle.setPosition(hit.position.x, hit.position.y, hit.position.z);
        this.reticle.setEulerAngles(0, 0, 0);
        this.reticleOpacity = 1;
        this.reticleVisibleUntil = performance.now() + this.reticleHoldMs;
        this.reticleMat.opacity = 0.85;
        this.reticleMat.update();
        this.reticle.enabled = true;
    }

    /** Get distance between two touch points */
    private getTouchDistance(touches: TouchList): number {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
    }

    /** Get angle between two touch points */
    private getTouchAngle(touches: TouchList): number {
        return Math.atan2(
            touches[1].clientY - touches[0].clientY,
            touches[1].clientX - touches[0].clientX,
        );
    }
}
